/* 2026-10-22 ardam: "Check we deliver to you" (snippets/delivery-postcode-check.liquid).
   Uses Shopify's cart shipping-rates API, which asks the same carrier service (Bespoke) that checkout uses, so the
   answer matches checkout exactly. No rates -> apology pop-up. Errors never block anything (checkout decides).
   IMPORTANT: only go live once every state is in the Shopify shipping zone (TAS + NT added). Shopify remembers the
   checked address for the visitor's session, and an address OUTSIDE the zone makes every product "sold out" for that
   session (found in preview testing, 21 Oct).
   Quiet by design: results are remembered for the browser session, a hidden cart drawer never checks by itself, and the
   automatic pop-up shows at most once per postcode per session (pressing Check always shows it). */
(() => {
  if (customElements.get('ardam-delivery-check')) return;
  const KEY = 'ardam_delivery_postcode';
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const store = {
    get: (s, k) => { try { return s.getItem(k); } catch (e) { return null; } },
    set: (s, k, v) => { try { s.setItem(k, v); } catch (e) {} },
  };
  const state = (pc) => {
    const n = parseInt(pc, 10);
    if ((n >= 200 && n <= 299) || (n >= 2600 && n <= 2618) || (n >= 2900 && n <= 2920)) return 'Australian Capital Territory';
    if (n >= 800 && n <= 999) return 'Northern Territory';
    if ((n >= 1000 && n <= 2599) || (n >= 2619 && n <= 2899) || (n >= 2921 && n <= 2999)) return 'New South Wales';
    if ((n >= 3000 && n <= 3999) || (n >= 8000 && n <= 8999)) return 'Victoria';
    if ((n >= 4000 && n <= 4999) || (n >= 9000 && n <= 9999)) return 'Queensland';
    if (n >= 5000 && n <= 5999) return 'South Australia';
    if (n >= 6000 && n <= 6999) return 'Western Australia';
    if (n >= 7000 && n <= 7999) return 'Tasmania';
    return null;
  };
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  async function countRates(pc) {
    const q = `shipping_address[zip]=${encodeURIComponent(pc)}&shipping_address[country]=Australia&shipping_address[province]=${encodeURIComponent(state(pc))}`;
    const prep = await fetch(`${root}cart/prepare_shipping_rates.json?${q}`, { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (prep.status === 422) return 0; // Shopify refuses the address for this cart: nothing ships there
    if (!prep.ok) throw new Error(`prepare ${prep.status}`);
    for (let i = 0; i < 20; i++) {
      const r = await fetch(`${root}cart/async_shipping_rates.json?${q}`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (r.status === 422) return 0;
      if (r.ok) {
        const body = await r.json();
        if (body && Array.isArray(body.shipping_rates)) return body.shipping_rates.length;
      } else if (r.status !== 202) {
        throw new Error(`rates ${r.status}`);
      }
      await sleep(500);
    }
    throw new Error('timeout');
  }

  function showSorry(pc, hasNonTreat) {
    let d = document.getElementById('ArdamDeliveryDialog');
    if (!d) {
      d = document.createElement('dialog');
      d.id = 'ArdamDeliveryDialog';
      d.className = 'ardam-delivery-dialog';
      document.body.appendChild(d);
    }
    d.innerHTML = `
      <h2>Sorry, we don't deliver to ${pc} yet</h2>
      <p>Our fresh and frozen raw food can't reach your area at the moment. We're sorry about that.</p>
      ${hasNonTreat ? '<p><strong>Good news:</strong> our Chomperz treats are posted Australia-wide, including Tasmania. Order them on their own and we\'ll post them to you.</p>' : ''}
      <p>Questions? Email <a href="mailto:team@ardam.com.au">team@ardam.com.au</a>.</p>
      <button type="button" class="button button--regular button--solid">OK</button>`;
    d.querySelector('button').addEventListener('click', () => d.close());
    if (typeof d.showModal === 'function') { if (!d.open) d.showModal(); } else d.setAttribute('open', '');
  }

  const say = (el, n, pc) => {
    el.result.textContent = n > 0 ? `✓ We deliver to ${pc}. Delivery options are shown at checkout.` : `Sorry, we can't deliver this order to ${pc}.`;
  };

  class ArdamDeliveryCheck extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.result = this.querySelector('.ardam-delivery-check__result');
      const saved = store.get(localStorage, KEY) || '';
      if (saved) this.input.value = saved;
      this.querySelector('form').addEventListener('submit', (e) => { e.preventDefault(); this.check(true); });
      this.onCartUpdated = async () => {
        // the theme re-rendered or changed the cart: refresh this element's cart signature, then re-check quietly
        try {
          const c = await fetch(`${root}cart.js`, { credentials: 'same-origin' }).then((r) => r.json());
          this.dataset.cart = c.items.map((i) => `${i.variant_id}:${i.quantity},`).join('');
        } catch (e) { return; }
        if (this.result.textContent && this.offsetParent !== null) this.check(false);
      };
      document.addEventListener('cart-updated', this.onCartUpdated, true); // the theme's event doesn't bubble: capture it
      if (!saved) return;
      const cached = store.get(sessionStorage, `ardam_dc|${this.dataset.cart}|${saved}`);
      if (cached !== null) { say(this, +cached, saved); return; }
      if (this.offsetParent !== null) this.check(false); // visible (cart page / open drawer) and not yet checked this session
    }
    disconnectedCallback() { document.removeEventListener('cart-updated', this.onCartUpdated, true); }
    async check(userAsked) {
      const pc = (this.input.value || '').trim();
      if (!/^\d{4}$/.test(pc) || !state(pc)) { if (userAsked) this.result.textContent = 'Please enter a 4-digit Australian postcode.'; return; }
      store.set(localStorage, KEY, pc);
      const key = `ardam_dc|${this.dataset.cart}|${pc}`;
      this.result.textContent = 'Checking…';
      try {
        const cached = store.get(sessionStorage, key);
        const n = cached !== null ? +cached : await countRates(pc);
        store.set(sessionStorage, key, String(n));
        say(this, n, pc);
        if (n > 0) return;
        const shownKey = `ardam_dc_shown|${pc}`;
        if (userAsked || !store.get(sessionStorage, shownKey)) { store.set(sessionStorage, shownKey, '1'); showSorry(pc, this.dataset.hasNonTreat === 'true'); }
      } catch (e) {
        this.result.textContent = "We couldn't check just now. You'll see delivery options at checkout.";
      }
    }
  }
  customElements.define('ardam-delivery-check', ArdamDeliveryCheck);
})();
